import { SITE_URL } from "@/lib/site-url";

/**
 * Metadatos SSR (title/description/og/twitter/canonical/hreflang) emitidos
 * desde el `head()` de cada ruta.
 *
 * Por qué existe: el `head()` global de `__root.tsx` emite los valores en
 * español como defaults. Si una ruta no declara los suyos, los rastreadores
 * (WhatsApp, LinkedIn, Telegram) se quedan con esas etiquetas y muestran la
 * vista previa en español aunque la URL sea `/en/...`.
 *
 * Los textos replican los que ya usan las páginas (`<SEO />` / `useSEO`) para
 * que el HTML servido y el que pinta React coincidan.
 */

export interface PageMetaCopy {
  pathEs: string;
  pathEn: string;
  title: { es: string; en: string };
  description: { es: string; en: string };
  keywords?: { es: string; en: string };
  imageAlt?: { es: string; en: string };
  /** Ruta pública de la imagen OG (1200x630). Por defecto la genérica. */
  image?: string;
  noindex?: boolean;
}

export const PAGE_META = {
  home: {
    pathEs: "/",
    pathEn: "/en",
    title: {
      es: "Retail Lemon | Gestión para comercios a pie de calle",
      en: "Retail Lemon | Complete Retail Management & Support",
    },
    description: {
      es: "Panel 360°, hoja de ruta y Agente Retail IA para tu tienda a pie de calle. Básico 39 €, Club Pro 69 € y Avanzado 99 €/mes, sin permanencia.",
      en: "360° dashboard, roadmap and Retail AI Agent for your street-level shop. Basic €39, Club Pro €69 and Advanced €99/month, no commitment.",
    },
    image: "/assets/og/og-home.jpg",

    keywords: {
      es: "panel 360 retail, agente retail ia, club pro retail, gestión tienda, hoja de ruta retail, subvenciones comercio",
      en: "360 retail panel, Retail AI Agent, club pro retail, store management, retail roadmap, retail grants",
    },
    imageAlt: {
      es: "Club Pro: panel 360 y Agente Retail IA para comercios locales — Retail Lemon",
      en: "Club Pro 360 panel and Retail AI Agent for local retail — Retail Lemon",
    },
  },
  mentoring: {
    pathEs: "/mentoria-retail",
    pathEn: "/en/mentoring",
    title: {
      es: "Mentor de Retail: Asesoría Personalizada | Retail Lemon",
      en: "Retail Mentor: Personalized Advisory | Retail Lemon",
    },
    description: {
      es: "Mentor de retail dedicado a tu proyecto. Extra de pago del plan Avanzado (99 €/mes), con precio antes de pagar. Resultados medibles y experiencia de trinchera.",
      en: "Dedicated retail mentor for your project. Paid extra on the Advanced plan (€99/month), price shown before paying. Measurable results, field-tested expertise.",
    },
    keywords: {
      es: "consultoría retail precios, mentoría retail estratégico, asesoramiento retail, dirección retail externalizada",
      en: "retail consulting pricing, strategic retail mentoring, retail advisory, outsourced retail management",
    },
  },
  insights: {
    pathEs: "/insights",
    pathEn: "/en/insights",
    title: {
      es: "Insights Retail · Artículos y Guías — Retail Lemon",
      en: "Retail Insights · Articles & Guides — Retail Lemon",
    },
    description: {
      es: "Artículos, análisis y guías tácticas sobre operaciones retail, M&A y estrategia para cadenas, inversores y PYMEs de retail.",
      en: "Articles, analysis and tactical guides on retail operations, M&A and strategy for retail chains, investors and SMBs.",
    },
    keywords: {
      es: "insights retail, artículos retail, guías retail, consultoría negocios, M&A retail, operaciones retail",
      en: "retail insights, retail articles, retail guides, business consulting, retail M&A, retail operations",
    },
    imageAlt: {
      es: "Insights de Retail — Artículos y guías de consultoría retail",
      en: "Retail Insights — Articles and retail consulting guides",
    },
  },
  calculator: {
    pathEs: "/herramientas/calculadora",
    pathEn: "/en/tools/calculator",
    title: {
      es: "Calculadora de rentabilidad retail | Retail Lemon",
      en: "Retail Profitability & Margin Calculator | Retail Lemon",
    },
    description: {
      es: "Calcula gratis el punto de equilibrio, margen comercial y viabilidad financiera de tu comercio o local a pie de calle.",
      en: "Free online calculator to measure break-even point, retail profit margin, and financial viability for your shop.",
    },
    image: "/assets/og/og-calculadora.jpg",
    keywords: {
      es: "calculadora rentabilidad retail, márgenes producto, stock, punto de equilibrio, tasa de esfuerzo, alquiler comercial, viabilidad tienda",
      en: "retail profitability calculator, product margins, stock, break-even, rent effort rate, commercial rent, store viability",
    },
    imageAlt: {
      es: "Calculadora de rentabilidad retail por metro cuadrado — herramienta gratuita para tiendas",
      en: "Retail profitability calculator per square meter — free tool for stores",
    },
  },
  locationAnalysis: {
    pathEs: "/diagnostico-locales-comerciales",
    pathEn: "/en/retail-location-analysis",
    title: {
      es: "Diagnóstico de locales comerciales | Retail Lemon",
      en: "Retail Location Analysis | Retail Lemon",
    },
    description: {
      es: "Analiza el riesgo financiero de tu próximo local. Cruza datos de tráfico, renta y competencia con nuestro motor de Location Intelligence.",
      en: "Analyze the financial risk of your next commercial space. Cross-reference foot traffic, rent, and local market data with our Location Intelligence engine.",
    },
    keywords: {
      es: "diagnóstico locales comerciales, análisis viabilidad local, location intelligence retail, riesgo alquiler comercial, evaluación tienda",
      en: "retail location analysis, commercial lease viability, location intelligence, foot traffic analysis, commercial real estate risk",
    },
    imageAlt: {
      es: "Diagnóstico de locales comerciales — análisis de viabilidad y riesgo retail",
      en: "Retail location analysis — viability and risk assessment tool",
    },
  },
  thermometer: {
    pathEs: "/detector-de-fugas",
    pathEn: "/en/leak-detector",
    title: {
      es: "Detector de Fugas Completo para comercios | Retail Lemon",
      en: "Complete Leak Detector for local shops | Retail Lemon",
    },
    description: {
      es: "Diagnóstico gratis de entrada: alquiler, personal, género, stock, equipo y ventas. Para tiendas, bares, cafeterías y locales. Resultado a fondo en Club Pro.",
      en: "Free entry diagnosis: rent, staff, goods, stock, team and sales. For shops, bars, cafés and premises. In-depth result in Club Pro.",
    },
    image: "/assets/og/og-termometro.jpg",
    keywords: {
      es: "detector de fugas comercio, diagnóstico retail gratis, alquiler porcentaje facturación, coste personal hostelería, food cost, rotación stock",
      en: "shop leak detector, free retail diagnosis, rent share of revenue, hospitality staff cost, food cost, stock rotation",
    },
  },
  chains: {
    pathEs: "/cadenas-y-franquicias",
    pathEn: "/en/chains-and-franchises",
    title: {
      es: "Cadenas y Franquicias Retail | Retail Lemon",
      en: "Retail Chains & Franchises | Retail Lemon",
    },
    description: {
      es: "Estandarizamos la operativa de tu cadena de tiendas: auditoría de red, SOPs, capacitación de Area Managers y rollout de mejoras para escalar con rentabilidad.",
      en: "We standardize your retail chain operations: network audit, SOPs, Area Manager training, and rollout of improvements to scale profitably.",
    },
    keywords: {
      es: "consultoría cadenas retail, franquicias retail, área manager, auditoría de tiendas, SOP retail, rollout retail",
      en: "retail chain consulting, retail franchise, area manager, store audit, retail SOP, retail rollout",
    },
  },
  familyOffices: {
    pathEs: "/family-offices-inversores",
    pathEn: "/en/family-offices-investors",
    title: {
      es: "Consultoría Retail para Inversores | Retail Lemon",
      en: "Retail Consulting for Investors | Retail Lemon",
    },
    description: {
      es: "Operational Due Diligence, Turnaround Management e Interim Operations para fondos y family offices con activos en retail. Validamos sinergias y cuantificamos potencial real de EBITDA.",
      en: "Operational Due Diligence, Turnaround Management, and Interim Operations for funds and family offices with retail assets. We validate synergies and quantify real EBITDA potential.",
    },
    keywords: {
      es: "due diligence retail, turnaround retail, interim management retail, family office retail, inversores retail, ebitda retail",
      en: "retail due diligence, retail turnaround, retail interim management, retail family office, retail investors, retail ebitda",
    },
  },
  contact: {
    pathEs: "/contacto",
    pathEn: "/en/contact",
    title: {
      es: "Contacto — Retail Lemon",
      en: "Contact — Retail Lemon",
    },
    description: {
      es: "¿Tienes una idea, una duda o simplemente quieres charlar sobre tu marca? Escríbenos y te respondemos en menos de 24 h.",
      en: "Have an idea, a question, or just want to chat about your brand? Write to us and we'll reply within 24 hours.",
    },
    keywords: {
      es: "contacto retail lemon, asesoría retail, consultoría tienda, mentoring retail contacto",
      en: "contact retail lemon, retail consulting, store consulting, retail mentoring contact",
    },
    imageAlt: {
      es: "Contacto Retail Lemon — Asesoría y mentoring para tiendas retail",
      en: "Contact Retail Lemon — Retail store consulting and mentoring",
    },
  },
  faq: {
    pathEs: "/faq",
    pathEn: "/en/faq",
    title: {
      es: "Preguntas frecuentes sobre planes y herramientas | Retail Lemon",
      en: "Plans and tools FAQ | Retail Lemon",
    },
    description: {
      es: "Resuelve tus dudas sobre Básico, Club Pro y Avanzado: precios, prueba de 7 días, cancelación, herramientas y extras.",
      en: "Answers about Basic, Club Pro and Advanced: prices, 7-day trial, cancellation, tools and extras.",
    },
    keywords: {
      es: "retail lemon preguntas frecuentes, retail lemon panel, retail lemon mentoría, retail lemon seguridad, retail lemon funcionamiento, retail lemon comercio",
      en: "retail lemon faq, retail lemon panel, retail lemon mentoring, retail lemon security, retail lemon how it works, retail lemon store",
    },
    imageAlt: {
      es: "Preguntas Frecuentes de Retail Lemon — Panel, metodología y soporte",
      en: "Retail Lemon Frequently Asked Questions — Panel, methodology and support",
    },
  },
  everythingForShop: {
    pathEs: "/todo-para-tu-comercio",
    pathEn: "/en/everything-for-your-store",
    title: {
      es: "Todo para tu comercio | Retail Lemon",
      en: "Everything for your store | Retail Lemon",
    },
    description: {
      es: "Con el Club Pro accedes a proveedores y servicios seleccionados para tu tienda: marketing, packaging, seguros, abogados, reformas y seguridad. 69€/mes.",
      en: "With Club Pro you access curated suppliers and services for your store: marketing, packaging, insurance, lawyers, fit-out and security. €69/month.",
    },
    keywords: {
      es: "proveedores para tiendas, servicios para comercios, packaging tienda, seguros comercio, abogados retail, reformas locales",
      en: "suppliers for stores, retail services, store packaging, shop insurance, retail lawyers, store fit-out",
    },
    imageAlt: {
      es: "Todo para tu comercio — proveedores y servicios del Club Pro de Retail Lemon",
      en: "Everything for your store — Retail Lemon Club Pro suppliers and services",
    },
  },
  legalNotice: {

    pathEs: "/aviso-legal",
    pathEn: "/en/legal-notice",
    title: { es: "Aviso Legal — Retail Lemon", en: "Legal Notice — Retail Lemon" },
    description: {
      es: "Aviso legal y condiciones de uso del sitio web Retail Lemon.",
      en: "Legal notice and terms of use for the Retail Lemon website.",
    },

  },
  privacy: {
    pathEs: "/privacidad",
    pathEn: "/en/privacy-policy",
    title: { es: "Política de Privacidad — Retail Lemon", en: "Privacy Policy — Retail Lemon" },
    description: {
      es: "Conoce cómo Retail Lemon recoge, trata y protege tus datos personales conforme al RGPD.",
      en: "Learn how Retail Lemon collects, processes and protects your personal data under GDPR.",
    },
  },
  cookies: {
    pathEs: "/cookies",
    pathEn: "/en/cookies",
    title: { es: "Política de Cookies — Retail Lemon", en: "Cookie Policy — Retail Lemon" },
    description: {
      es: "Información sobre las cookies que utiliza Retail Lemon y cómo gestionarlas.",
      en: "Information about the cookies used by Retail Lemon and how to manage them.",
    },
  },
  grants: {
    pathEs: "/recursos/subvenciones",
    pathEn: "/en/resources/subsidies",
    title: {
      es: "Subvenciones para comercio y hostelería | Retail Lemon",
      en: "Retail Grants & Subsidies Finder | Retail Lemon",
    },
    description: {
      es: "Encuentra ayudas públicas, subvenciones de digitalización y fondos para el comercio minorista y restauración.",
      en: "Discover government grants, funding, and digitization subsidies tailored for retail businesses.",
    },
    image: "/assets/og/og-grants.jpg",
    keywords: {
      es: "alertas subvenciones email, notificaciones ayudas comercio españa, suscripcion subvenciones retail, avisos subvenciones pymes 2026, recibir alertas por correo",
      en: "email alerts retail grants, Spain grants notifications, retail subsidies 2026, SME grants Spain, subscribe to business grants",
    },
    imageAlt: {
      es: "Subvenciones y ayudas 2026 para comercios — Retail Lemon",
      en: "2026 grants and subsidies for retail businesses — Retail Lemon",
    },
  },
  terms: {
    pathEs: "/condiciones-contratacion",
    pathEn: "/en/terms",
    title: { es: "Condiciones de Contratación — Retail Lemon", en: "Terms of Service — Retail Lemon" },
    description: {
      es: "Condiciones de los planes Básico, Club Pro y Avanzado: pago mensual sin permanencia, prueba de 7 días, cambios de plan, cancelación y extras.",
      en: "Terms for the Basic, Club Pro and Advanced plans: monthly billing with no commitment, 7-day trial, plan changes, cancellation and extras.",
    },

  },
  toolsHub: {
    pathEs: "/herramientas",
    pathEn: "/en/tools",
    title: {
      es: "Herramientas para retail y hostelería | Retail Lemon",
      en: "Tools for retail and hospitality | Retail Lemon",
    },
    description: {
      es: "Herramientas gratuitas para comercios y hostelería: calculadora de rentabilidad, detector de fugas, subvenciones, Agente Retail IA y música para tu local.",
      en: "Free tools for shops and hospitality: profitability calculator, leak detector, grants, Retail AI Agent and music for your store.",
    },
    keywords: {
      es: "herramientas para comercios, herramientas retail, herramientas hostelería, calculadora rentabilidad tienda, diagnóstico de local",
      en: "tools for shops, retail tools, hospitality tools, store profitability calculator, store diagnosis",
    },
    image: "/assets/og/og-tools-hub.jpg",
    imageAlt: {
      es: "Herramientas para comercios y hostelería de Retail Lemon",
      en: "Retail Lemon tools for shops and hospitality",
    },
  },
} as const satisfies Record<string, PageMetaCopy>;

export type PageMetaKey = keyof typeof PAGE_META;

const OG_IMAGE = `${SITE_URL}/og-retail-lemon.jpg`;

export function pageHead(
  key: PageMetaKey,
  lang: "es" | "en",
  /**
   * `links: false` para páginas que ya emiten canonical + hreflang con
   * `<SEO />` (Helmet, también en SSR). Evita duplicar <link rel="canonical">.
   */
  opts: { links?: boolean } = {},
) {
  const copy: PageMetaCopy = PAGE_META[key];
  const path = lang === "es" ? copy.pathEs : copy.pathEn;
  const url = `${SITE_URL}${path}`;
  const esUrl = `${SITE_URL}${copy.pathEs}`;
  const enUrl = `${SITE_URL}${copy.pathEn}`;
  const title = copy.title[lang];
  const description = copy.description[lang];
  const imageAlt = copy.imageAlt?.[lang] ?? title;
  const ogImage = copy.image ? `${SITE_URL}${copy.image}` : OG_IMAGE;

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:type", content: "website" },
    { property: "og:locale", content: lang === "es" ? "es_ES" : "en_US" },
    { property: "og:locale:alternate", content: lang === "es" ? "en_US" : "es_ES" },
    { property: "og:image", content: ogImage },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: imageAlt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:url", content: url },
    { name: "twitter:image", content: ogImage },
    { name: "twitter:image:alt", content: imageAlt },
    {
      name: "robots",
      content: copy.noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1",
    },
  ];

  if (copy.keywords) meta.push({ name: "keywords", content: copy.keywords[lang] });

  if (opts.links === false) return { meta };

  return {
    meta,
    links: [
      { rel: "canonical", href: url },
      { rel: "alternate", hrefLang: "es", href: esUrl },
      { rel: "alternate", hrefLang: "en", href: enUrl },
      { rel: "alternate", hrefLang: "x-default", href: esUrl },
    ],
  };
}
